import { emptyGUID } from "components/Admin/Organizations/OrganizationDetail/OrganizationModules/OrganizationModuleDetails/OrganizationModuleQuestionDependencies";
import { GroupedTags } from "components/Evaluation/Stores/EvalStore";
import {
    generateClipForConversation,
    matchTranscriptionToSoundClip,
    soundClipDurationDisplay,
    textClipTimestampDisplay,
} from "components/Evaluation/Utils";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import type {
    PartialSegment,
    PlayerOptions,
} from "components/SoundClipEditor/SoundClipEditor";
import { camelCase, startCase, uniqBy } from "lodash";
import {
    action,
    computed,
    makeObservable,
    observable,
    reaction,
    runInAction,
} from "mobx";
import { InteractionHierarchyLevels } from "models/InteractionHierarchyLevels";
import { InteractionType } from "models/InteractionType";
import SoundClip from "models/SoundClip";
import { StorageAccountUseOptions } from "models/StorageAccount";
import { Moment } from "moment";
import AgentService from "services/AgentService";
import type {
    ConversationData,
    ConversationSegmentItem,
    ConversationSoundClip,
} from "services/ConversationService";
import {
    ConversationService,
    VisibleEvalModule,
} from "services/ConversationService";
import { OrganizationService } from "services/OrganizationService";
import { SoundClipService } from "services/SoundClipService";
import {
    ENGLISH_LANGUAGE_CODE,
    IMultiLanguageTranscriptionPayload,
    ITranscriptionSegment,
    ManualTranscriptRedactionType,
    TranscriptionService,
} from "services/TranscriptionService";
import { AuthStore } from "stores/AuthStore";
import { BaseStore } from "stores/BaseStore";
import {
    DateRangeLabel,
    DateReferenceOption,
} from "stores/ComponentStores/DatePickerComponentStore";
import { DialogComponentStore } from "stores/ComponentStores/DialogComponentStore";
import type { IRootStore } from "stores/RootStore";
import { AcxStore } from "stores/RootStore";
import { isNullableType } from "utils/TypeGuards";
import {
    computeAverageUtteranceForTranscript,
    getTranscriptByLangCode,
} from "utils/Utils";
import LocalStorage from "../../../stores/LocalStorage";
import RootStore from "../../../stores/RootStore";

import { v4 as uuidv4 } from "uuid";
import {
    addtlMetadataCatTitle,
    aiFilterCatTitle,
    basicFilterCatTitle,
    ConversationFilter,
    ConversationFilterCategory,
    localStorageFilterInputKey,
} from "../Views/Drawer/components/ManageFilterInputDrawer";

import { IExtendedMetadataFilterItem } from "components/ManagerInteractions/Stores/ManagerInteractionsStore";
import SoundClipEditorStore from "components/SoundClipEditor/Stores/SoundClipEditorStore";
import { SpeakerStore } from "components/UI/AcxTranscription/Stores/SpeakerStore/SpeakerStore";
import { formatSegmentHeading } from "components/UI/AcxTranscription/utils";
import AudioMetadataModel from "models/AudioMetadataModel";
import Conversation from "models/Conversation";
import Evaluation from "models/Evaluation";
import { ServiceError } from "services/BaseService";
import { EvaluationService } from "services/EvaluationService";
import ClassifierService from "services/ClassifierService";
import {
    ClassifierTypeName,
    MetadataService,
    coreFields,
} from "services/MetadataService";
import { MetaLabelService } from "services/MetaLabelService";
import { UserObjectTrackingService } from "services/UserObjectTrackingService";
import {
    ApplicationFiltersStore,
    LanguageFilterObject,
    PrimaryTopic,
    SavedFilter,
} from "stores/ApplicationFilters/ApplicationFiltersStore";
import {
    ApplicationFilterItemsStore,
    LEGACY_FILTER_GROUP_TITLE,
    type FilterKey,
} from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import RBCFilterStore, {
    IRBCFilter,
} from "../Views/Drawer/components/DeepFilter/RBCFilterStore";
import WordsPhraseStore, {
    IWordsPhrasesModel,
} from "../Views/Drawer/components/DeepFilter/WordsPhrasesStore";
import ConvoWorkflowStore from "../Views/Main/FocusedConversation/components/convoSidePanel/store/ConvoWorkflowStore";
import ConversationLabelingStore from "../Views/Main/FocusedConversation/components/convoSidePanel/store/ConversationLabelingStore";
import {
    ML_SpeakerIdentification,
} from "../Views/Drawer/components/ConversationClassifierChipList/Helpers";
import { PatientJourneyStage } from "stores/ApplicationFilters/Filters/PatientJourneyStage";
import { signalDataToGoogleFormat } from "utils/ReportDataToGoogleFormat";
import { OrganizationMetadataField } from "models/OrganizationModels/OrganizationMetadataField";
import { AcxTranscriptionStore } from "components/UI/AcxTranscription/Stores/AcxTranscriptionStore";
import _ from "lodash";
import { SafetyEvent } from "stores/ApplicationFilters/Filters/SafetyEvent";
import { AgentCoachingService } from "services/AgentCoachingService";
import {
    AgentCoaching,
    AgentCoachingStatus,
    AgentCoachingTarget,
    targetedConversationIds,
    targetedEvaluationIds,
} from "models/AgentModels/AgentCoaching";
import {
    CoachingLockStatus,
    EvalReviewStore,
} from "components/Agents/EvalReview/Stores/EvalReviewStore";
import { dataPointTooltipBuilder } from "components/Signals/BaseObjects/ChartCard/DataPointTooltips";
import { temporalGrain } from "components/Signals/BaseObjects/DataAggregation";
import { IChatRecord } from "components/UI/Chat/AcxChatViewer";
import {
    getFocusedConversationInsights,
    getLegacyTopicInsights,
} from "../FocusedConversationInsights";

const createAudioClipTask = "Creating Audio Clip";
export const loadConversationSoundClips = "Loading Conversation Sound Clips";
const UNREDACTED_ENGLISH_LANGUAGE_CODE = "en-unredacted";

/**
 * this is actually for the primary theme filter
 */
interface TopicsFilter {
    topics: { id: string; topicLabel: string }[];
    /**
     * true for contains, false for does not contains
     */
    contains: boolean;
}

interface PrimaryTopicsFilter {
    topics: PrimaryTopic[];
    /**
     * true for contains, false for does not contains
     */
    contains: boolean;
}

export interface ConversationFilterObject {
    agentId: string;
    agentIds: string[];
    callDuration?: number[];
    qualityEvalScore?: number[];
    searchPhrase: string;
    hasEddyEffect?: boolean;
    hasEval?: boolean;
    evalType: null | EvalType;
    dateRangeLabel: DateRangeLabel;
    RBCFilterItems: IRBCFilter[];
    topicsFilter: TopicsFilter;
    primaryTopicsFilter: PrimaryTopicsFilter;
    hierarchyIds: string[];
    hipaa: "Compliant" | "Not Compliant" | "N/A" | null;
    begSentiment: "Negative" | "Neutral" | "Positive" | null;
    endSentiment: "Negative" | "Neutral" | "Positive" | null;
    selectedSortOption?: SortOption;
    mediaType: ConversationMediaTypes[];
    callDirection: ConversationCallDirections[];
    clientCallId?: string;
    safetyEvent?: SafetyEvent;
    meta1?: string;
    meta2?: string;
    meta3?: string;
    meta4?: string;
    meta5?: string;
    extendedMetadataFields?: IExtendedMetadataFilterItem[];
    contactTypes: string[];
    patientJourneyStages: PatientJourneyStage[];
    languages: LanguageFilterObject[];
}

type ConversationMediaTypes = "Audio" | "ProcessedChat";
type ConversationCallDirections = "Inbound" | "Outbound" | "Transfer";

export type BackupFilters = {
    orgId: string;
    filterObject: ConversationFilterObject;
    beginDate: Moment;
    endDate: Moment;
    referenceOption: DateReferenceOption;
    wordsPhrases: {
        items: IWordsPhrasesModel[];
        isInputStandard: boolean;
    };
};

export interface SortOption {
    label: JSX.Element;
    value: string;
    sortField: "ArrivedOn" | "Timestamp" | null; // TODO: add call duration
    sortDirection: "asc" | "desc" | null;
}

/** Legacy ML-Topic-LLM result shape retained for Signals drilldown parsing. */
export type Topics = {
    topic1: string | null;
    topic2: string | null;
    topic3: string | null;
    topic1Reasoning: string | null;
    topic2Reasoning: string | null;
    topic3Reasoning: string | null;
};

export enum EvalType {
    BottomOfTheFunnel = "BottomOfTheFunnel",
    MiddleOfTheFunnel = "MiddleOfTheFunnel",
}

export const loadConversations = "Load Conversations";
export const loadConversation = "Load Conversation";
export const loadCreateClip = "Create Conversation Clip";
export const loadDeleteClip = "Deleting Conversation Clip";
export const loadCurrentEval = "Loading Evaluation for Conversation";
export const loadAgentCoaching = "Loading Agent Coaching";
const loadConversationSegments = "Loading Conversation Segments";

export enum SidePanelType {
    Hidden,
    Workflow,
    DataLabeling,
    Coaching,
}

type OpenCoachingPanelOptions = {
    refreshCoaching?: boolean;
    retryLock?: boolean;
};

export type FilterType = {
    title: string;
    isActive?: boolean;
    key?: string;
    needsPermission?: boolean;
    /**
     * The Application Filter this drawer entry renders. Required, because the drawer identifies
     * filters by `storeObjectKey` — which is derived from the title and is not the filter key — so
     * without this there is no route from a drawer entry back to its declaration in
     * `ApplicationFilterItemsStore`. That declaration owns the filter's status and info text, and a
     * new filter added here should not be able to silently lose them.
     */
    filterKey: FilterKey;
};

const commonlyUsedFilters: FilterType[] = [
    {
        title: "Interaction Date Range",
        isActive: true,
        key: "dateRange",
        filterKey: "dateRange",
    },
    { title: "Call Duration", isActive: true, filterKey: "callDuration" },
    {
        title: "Message Count",
        isActive: true,
        key: "messageCount",
        filterKey: "messageCount",
    },
    { title: "Hierarchy", isActive: true, filterKey: "hierarchyIds" },
    { title: "Classifiers", isActive: true, filterKey: "rbcFilterItems" },
    {
        title: "Words Or Phrases",
        isActive: true,
        filterKey: "wordsAndPhrasesSearchString",
    },
    { title: "Evaluation Type", isActive: true, filterKey: "evaluationTypes" },
    {
        title: "Conversation Labeling",
        needsPermission: true,
        filterKey: "dataLabels",
    },
    {
        title: "Media Type (calls, chats, or emails)",
        key: "mediaType",
        filterKey: "mediaTypes",
    },
    {
        title: "Date Type",
        key: "dateReferenceOption",
        filterKey: "dateReferenceOption",
    },
    { title: "Call Direction", filterKey: "callDirections" },
    { title: "Client Call Id", filterKey: "clientCallId" },
    { title: "Agents", filterKey: "agentIds" },
    { title: "Agent Managers", filterKey: "agentManagerIds" },
    { title: "Favorite Conversations", filterKey: "favoriteConversations" },
    {
        title: "AI-Powered Search",
        key: "semanticSearch",
        filterKey: "embeddedTranscriptionQuery",
    },
    { title: "Language", filterKey: "languages" },
    { title: "Module Scores", filterKey: "moduleScores" },
    { title: "Question Results", filterKey: "questionResults" },
];

const coreAIFilters: FilterType[] = [
    {
        title: "Eddy Effect Signal",
        isActive: true,
        filterKey: "eddyEffectStatus",
    },
    { title: "Starting Sentiment", filterKey: "beginningSentiment" },
    { title: "Ending Sentiment", isActive: true, filterKey: "endingSentiment" },
    { title: "Contact Type", isActive: true, filterKey: "contactTypes" },
    {
        title: "Patient Journey",
        isActive: true,
        filterKey: "patientJourneyStages",
    },
    { title: "Outline", isActive: true, filterKey: "outlineSegmentFilterItems" },
    { title: "Model Results", isActive: true, filterKey: "modelResults" },
    // Both Theme filters are titled the same here, matching the Signals filter picker; the status
    // tag is what tells them apart. Titles are display-only in this drawer — storeObjectKey is the
    // identity used for rendering, permissions, and collapse state — so the legacy one pins its
    // historical "primaryTheme" key rather than deriving a colliding one from the shared title.
    // filterKey is what routes each back to its own declaration, and the two differ there.
    {
        title: "Primary Theme",
        key: "primaryTheme",
        isActive: true,
        filterKey: "topics",
    },
    {
        title: "Primary Theme",
        key: "themes",
        isActive: true,
        filterKey: "themes",
    },
    { title: "Primary Topic", isActive: true, filterKey: "primaryTopics" },
    { title: "Primary Driver", isActive: true, filterKey: "drivers" },
    { title: "HIPAA Compliance", filterKey: "hipaaCompliance" },
    { title: "Safety Event", filterKey: "safetyEvent" },
];

const metadataFilters: FilterType[] = [
    { title: "Meta1", filterKey: "meta1" },
    { title: "Meta2", filterKey: "meta2" },
    { title: "Meta3", filterKey: "meta3" },
    { title: "Meta4", filterKey: "meta4" },
    { title: "Meta5", filterKey: "meta5" },
];

/**
 * Subtitle for the derived Legacy category. Its title comes from
 * {@link LEGACY_FILTER_GROUP_TITLE} so this section and the Signals filter picker cannot end up
 * labelled differently; only the subtitle is local, because the categories here carry one and the
 * groups there do not.
 */
const legacyFilterCatSubtitle = "Filters replaced by a newer filter";

const initFilterCategories: ConversationFilterCategory[] = [
    {
        title: basicFilterCatTitle,
        subtitle: "Commonly used filters",
        filters: commonlyUsedFilters.map((filter) => ({
            filterTitle: filter.title,
            isActive: filter.isActive ?? false,
            id: uuidv4(),
            storeObjectKey: filter.key ?? camelCase(filter.title),
            filterKey: filter.filterKey,
        })),
    },
    {
        title: aiFilterCatTitle,
        subtitle: "Filter by machine learning model identification",
        filters: coreAIFilters.map((filter) => ({
            filterTitle: filter.title,
            isActive: filter.isActive ?? false,
            id: uuidv4(),
            storeObjectKey: filter.key ?? camelCase(filter.title),
            filterKey: filter.filterKey,
        })),
    },
    {
        title: addtlMetadataCatTitle,
        subtitle: "Filter by extended metadata",
        filters: metadataFilters.map((filter) => ({
            filterTitle: filter.title,
            isActive: false,
            id: uuidv4(),
            storeObjectKey: camelCase(filter.title),
            filterKey: filter.filterKey,
        })),
    },
];

@AcxStore
export default class ConversationsStore extends BaseStore {
    readonly soundClipService: SoundClipService = new SoundClipService();
    private readonly conversationService = new ConversationService();
    private readonly transcriptionService = new TranscriptionService();
    readonly agentService = new AgentService();
    private readonly organizationService = new OrganizationService();
    private readonly evaluationService = new EvaluationService();
    private readonly agentCoachingService = new AgentCoachingService();
    private readonly metaLabelService: MetaLabelService =
        new MetaLabelService();
    private readonly metadataService: MetadataService = new MetadataService();
    private readonly classifierService = new ClassifierService();
    private readonly userObjectTrackingService =
        new UserObjectTrackingService();

    readonly authStore: AuthStore;
    manageSavedFilterDialogStore = new DialogComponentStore();
    manageSavedFilterCDStore = new DialogComponentStore();

    readonly RBCFilterStore: RBCFilterStore;
    readonly wordsPhrasesStore: WordsPhraseStore;

    private readonly applicationFiltersStore: ApplicationFiltersStore;
    private readonly applicationFilterItemsStore: ApplicationFilterItemsStore;

    messageStore: MessageStore;
    convoWorkflowStore: ConvoWorkflowStore;
    convoDataLabelStore: ConversationLabelingStore;
    evalReviewStore: EvalReviewStore;

    localStorage: LocalForage;
    defaultLocalStorage: LocalForage;

    readonly speakerStore: SpeakerStore;

    @observable
    transcriptionStore: AcxTranscriptionStore | null;

    @observable
    fromAgent: boolean = false;

    @observable
    soundClipEditorStore: SoundClipEditorStore;

    @observable
    filterInputManagerIsOpen: boolean = false;

    @observable
    conversationsList?: ConversationData[] = [];

    @computed get hasConversations() {
        return this.conversationsList && this.conversationsList.length > 0;
    }

    @observable sqlQueries: string[] | undefined;
    @computed get hasSqlQueries() {
        return this.sqlQueries && this.sqlQueries.length > 0;
    }

    @observable
    isInitialLoad?: boolean = false;

    @observable
    sidePanel?: SidePanelType = SidePanelType.Hidden;

    /**
     * this is also the audioMetadataId
     */
    @observable
    selectedConversationId?: string;

    /**
     * KBo will set this value if the user followed the "Analyze a sample ..."
     * then "Show me these calls in convos ..." flow.
     */
    @observable
    conversationIds?: string[];

    @observable
    estimatedConversationCount?: number;

    @observable
    selectedConversation: Conversation | null;

    private averageUtteranceDuration: number = 0.29;

    @observable
    pageNumber: number = 1;

    @observable
    groupClipTags: GroupedTags[] = [];

    @observable
    conversationSoundClips: ConversationSoundClip[] = [];

    @observable
    playerOptions: PlayerOptions = {
        isPlaying: false,
        startTime: 0,
        endTime: 0,
        id: "",
    };

    @observable
    audioClip: { status: "default" | "success" | "error" | "loading" } = {
        status: "default",
    };

    @observable
    selectedSortOption?: SortOption;

    @observable
    unsavedFilters: SavedFilter[] = [];

    @observable.shallow
    hierarchyLevels: InteractionHierarchyLevels[] | undefined;

    @observable
    stageDeleteClip: SoundClip | null = null;

    @observable
    evaluateConversationDialogOpen: boolean = false;

    @observable
    evaluateSuccessDialogOpen: boolean = false;

    @observable
    favoriteTrackedGroupId: string = "";

    @observable
    favoritedConversations: { [conversationId: string]: boolean };

    @observable
    allFilterCategories: ConversationFilterCategory[] = [];
    // Create a new AbortController for this store
    @observable
    abortController: AbortController = new AbortController();

    // for sorting classifier chips, we only want to update on apply
    @observable
    selectedContainsClassifierIds: string[] = [];

    @observable
    activeClip: SoundClip | undefined;

    @observable
    currentEval: Evaluation | null = null;

    @observable
    evaluationScores: VisibleEvalModule[] = [];

    @observable
    evalScoresLoading = false;

    @observable
    handleUnauthorizedError?: (e: ServiceError) => void;

    @observable
    loadConversationsFailed?: boolean;

    @observable
    loadConversationFailed?: boolean;

    // conversation over time chart data
    @observable
    conversationsOverTimeChartData?: any;

    @observable
    isConversationsOverTimeChartExpanded: boolean = true;

    @observable
    isCompareTotal?: boolean = false;

    @observable
    isAppliedFilterCountForLastRequestGreaterThanZero: boolean = false;

    @observable
    agentCoaching: AgentCoaching[] = [];

    // KBo can overlap calls to openCoachingPanelWithLock. A count prevents one
    // completed call from clearing the loading state while another is active.
    @observable
    private coachingPanelOpenRequestCount = 0;

    @observable
    conversationSegments: ConversationSegmentItem[] = [];

    constructor(public rootStore: IRootStore) {
        super("ConversationsStore");
        makeObservable(this);

        this.resetAbortController();

        this.authStore = rootStore.getStore(AuthStore);

        const local = RootStore().getStore(LocalStorage);
        this.localStorage = local.getLocalStore("ConversationsStore");
        this.defaultLocalStorage = local.getLocalStore();

        this.messageStore = rootStore.getStore(MessageStore);
        this.convoWorkflowStore = rootStore.getStore(ConvoWorkflowStore);
        this.convoDataLabelStore = rootStore.getStore(
            ConversationLabelingStore,
        );
        this.evalReviewStore = rootStore.getStore(EvalReviewStore);

        this.speakerStore = new SpeakerStore();
        this.transcriptionStore = null;

        this.applicationFiltersStore = rootStore.getStore(
            ApplicationFiltersStore,
        );
        this.applicationFilterItemsStore = rootStore.getStore(
            ApplicationFilterItemsStore,
        );

        reaction(
            () => this.authStore.orgStore.selectedOrganization,
            () => {
                this.resetConversations();
                this.resetAbortController();
                this.setFavoriteTrackedGroupId();
            },
            { fireImmediately: true },
        );

        reaction(
            () => this.selectedConversationId,
            (id) => {
                this.getSingleConversation(id);
                this.convoDataLabelStore.audioMetadataId = id;
            },
            { fireImmediately: true },
        );

        reaction(
            () => this.rootStore.activeLocation.location,
            (location, previousLocation) => {
                if (previousLocation && location !== previousLocation) {
                    const normalizedLocation = location
                        .split(/[?#]/)[0]
                        .replace(/\/+$/, "")
                        .toLowerCase();
                    const isEnteringStandaloneCoaching =
                        normalizedLocation ===
                        "/app/agentcoaching/evaluationreview";

                    if (this.convoDataLabelStore.hasDataLabelingLock) {
                        this.convoDataLabelStore.ReleaseDataLabelingLock();
                    }
                    if (
                        this.evalReviewStore.coachingLockStatus !==
                            CoachingLockStatus.Idle &&
                        !this.evalReviewStore.consumeCoachingLockNavigation(
                            location,
                        ) &&
                        !isEnteringStandaloneCoaching
                    ) {
                        this.evalReviewStore.releaseCoachingLock();
                    }
                }
            },
        );

        // extended metadata fields and getting metadata labels 1-5
        reaction(
            () => ({
                activeLocation: this.rootStore.activeLocation,
            }),
            this.debounceEffect(async (params) => {
                if (
                    params.activeLocation &&
                    !params.activeLocation.location.includes("conversations")
                ) {
                    this.resetAbortController();
                    return;
                }

                const localItem: string | undefined | null =
                    await this.localStorage.getItem(
                        localStorageFilterInputKey +
                            this.authStore.orgStore.selectedOrganization?.id,
                    );

                let categoriesToUse = initFilterCategories;

                if (localItem) {
                    const localParsedItem: ConversationFilterCategory[] =
                        JSON.parse(localItem);

                    // If there are local parsed items, merge them with the initial categories
                    if (localParsedItem && localParsedItem.length > 0) {
                        categoriesToUse = categoriesToUse.map((cat) => {
                            let updatedFilters = cat.filters;
                            let localFilters =
                                localParsedItem.find(
                                    (c) => c.title === cat.title,
                                )?.filters ?? [];

                            if (cat.title === aiFilterCatTitle) {
                                // If deprecated filters: safetyEvents or adverseEvent filter are active, new safetyEvent filter should be active.
                                const hasActiveSafetyEvent = localFilters.some(
                                    (f) =>
                                        [
                                            "safetyEvents",
                                            "adverseEvent",
                                        ].includes(f.storeObjectKey) &&
                                        f.isActive,
                                );

                                if (hasActiveSafetyEvent) {
                                    const localFiltersHasSEFilter =
                                        localFilters.some(
                                            (f) =>
                                                f.storeObjectKey ===
                                                "safetyEvent",
                                        );
                                    if (localFiltersHasSEFilter) {
                                        localFilters = localFilters.map((f) => {
                                            if (
                                                f.storeObjectKey ===
                                                "safetyEvent"
                                            ) {
                                                return {
                                                    ...f,
                                                    isActive: true,
                                                };
                                            }
                                            return f;
                                        });
                                    } else {
                                        const seFilter = cat.filters.find(
                                            (f) =>
                                                f.storeObjectKey ===
                                                "safetyEvent",
                                        );
                                        if (seFilter) {
                                            localFilters.push({
                                                ...seFilter,
                                                isActive: true,
                                            });
                                        }
                                    }
                                }

                                localFilters = localFilters.filter(
                                    (f) =>
                                        [
                                            "safetyEvents",
                                            "adverseEvent",
                                        ].includes(f.storeObjectKey) === false,
                                );
                            }

                            if (localFilters) {
                                const mergedMap = new Map<
                                    string,
                                    ConversationFilter
                                >(
                                    updatedFilters.map((filter) => [
                                        filter.storeObjectKey,
                                        filter,
                                    ]),
                                );
                                for (const localFilter of localFilters) {
                                    // The stored copy wins for user state — isActive is the user's
                                    // choice — but the title and filterKey belong to the
                                    // definition, so they are always taken from it. That is what
                                    // heals a filter renamed since the user last opened this
                                    // drawer, and an entry saved before filterKey existed, without
                                    // either needing a migration. A stored title would otherwise
                                    // pin whatever the filter was called the day it was written:
                                    // the Primary Driver Theme filter shipped as "Themes" before
                                    // AUT-10227 renamed it, and every user who loaded the drawer
                                    // in between would still be reading the old name.
                                    const defined = mergedMap.get(
                                        localFilter.storeObjectKey,
                                    );
                                    mergedMap.set(localFilter.storeObjectKey, {
                                        ...localFilter,
                                        filterTitle:
                                            defined?.filterTitle ??
                                            localFilter.filterTitle,
                                        filterKey:
                                            defined?.filterKey ??
                                            localFilter.filterKey,
                                    });
                                }
                                updatedFilters = Array.from(mergedMap.values());
                            }
                            return {
                                ...cat,
                                filters: updatedFilters,
                            };
                        });
                    }
                }

                this.localStorage.setItem(
                    localStorageFilterInputKey +
                        this.authStore.orgStore.selectedOrganization?.id,
                    JSON.stringify(categoriesToUse),
                );

                // remove filters based on user permissions
                categoriesToUse = categoriesToUse.map((category) => {
                    let filters = category.filters;

                    // No need to check additional metadata category
                    if (category.title !== addtlMetadataCatTitle) {
                        filters = filters.filter((filter) => {
                            switch (filter.storeObjectKey) {
                                // Standard Filters
                                case "conversationLabeling":
                                    return (
                                        this.authStore.canUserView(
                                            "Data Labeling",
                                        ) ||
                                        this.authStore.canUserView("Research")
                                    );
                                case "semanticSearch":
                                    return this.authStore.canUserView(
                                        "AI Search",
                                    );

                                // Core AI Models
                                case "safetyEvent":
                                    return this.authStore.canUserView(
                                        "Safety Events Model",
                                    );
                                case "patientJourney":
                                    return this.authStore.canUserView(
                                        "Patient Journey",
                                    );
                                case "modelResults":
                                    return this.authStore.canUserView(
                                        "Model Results",
                                    );
                                case "moduleScores":
                                    return (
                                        this.authStore.canUserView(
                                            "Module Scores",
                                        ) &&
                                        (!this.authStore.canUserView(
                                            "Restrict Evaluations",
                                        ) ||
                                            this.authStore.isUserUltra())
                                    );
                                case "evaluationType":
                                    return (
                                        !this.authStore.canUserView(
                                            "Restrict Evaluations",
                                        ) || this.authStore.isUserUltra()
                                    );
                                case "questionResults":
                                    return (
                                        !this.authStore.canUserView(
                                            "Restrict Evaluations",
                                        ) || this.authStore.isUserUltra()
                                    );
                                case "primaryTheme":
                                case "primaryTopic":
                                    return this.authStore.canUserView("Topics");
                                case "themes":
                                case "primaryDriver":
                                    return this.authStore.canUserView(
                                        "Drivers",
                                    );
                                case "hipaaCompliance":
                                    return this.authStore.canUserView(
                                        "HIPAA Compliance Model",
                                    );
                                default:
                                    return true;
                            }
                        });
                    }

                    return {
                        ...category,
                        filters,
                    };
                });

                // When navigating to this page, if filters are applied but not active show them temporarily
                this.setAllFilterCategories(
                    this.updateTemporaryFilters(categoriesToUse),
                );

                this.loadMetadata(
                    this.authStore.orgStore.selectedOrganization?.id,
                );

                this.loadExtendedMetadata(
                    this.authStore.orgStore.selectedOrganization?.id,
                );
            }, 500),
            { delay: 0, fireImmediately: true },
        );

        reaction(
            () => ({
                evalId: this.selectedConversation?.evaluationId,
            }),
            async ({ evalId }) => {
                if (evalId && evalId !== emptyGUID) {
                    try {
                        this.evalScoresLoading = true;
                        const res =
                            await this.conversationService.getVisibleEvalModulesForCompletedEvaluation(
                                evalId,
                            );
                        this.evaluationScores = res;
                    } catch (e) {
                        console.error(e);
                    } finally {
                        this.evalScoresLoading = false;
                    }
                } else {
                    this.evaluationScores = [];
                }
            },
        );
    }

    public resetAbortController() {
        this.abortController = new AbortController();
    }

    public cancelConversationsRequests() {
        this.abortController.abort();
    }

    @action
    setFavoriteTrackedGroupId() {
        this.setupAsyncTask("Load Favorite Tracked Group ID", async () => {
            this.favoriteTrackedGroupId = (
                await this.userObjectTrackingService.getUserTrackedGroupByName(
                    "Favorite",
                )
            ).id;
        });
    }

    @action
    setTranscriptionStore(newTranscriptionStore: AcxTranscriptionStore) {
        this.transcriptionStore = newTranscriptionStore;
    }

    @action
    onRedactSelection = async (
        redactionType: ManualTranscriptRedactionType,
        selectedChannel?: number,
        selectedText?: string,
        selectedStartTime?: number,
        selectedEndTime?: number,
    ) => {
        const selection = this.transcriptionStore?.lastSelection;
        // Speaker-scoped timestamps become the canonical transcript/audio range
        // when the dialog is resolving a multi-speaker selection.
        const startTime =
            selectedStartTime ?? selection?.selectedStartTimestamp;
        const endTime = selectedEndTime ?? selection?.selectedEndTimestamp;

        if (
            !this.selectedConversationId ||
            !this.selectedConversation ||
            !selection ||
            startTime === null ||
            startTime === undefined ||
            endTime === null ||
            endTime === undefined
        ) {
            throw new Error("No transcript selection is available to redact.");
        }

        await this.transcriptionService.manualTranscriptRedaction({
            audioMetadataId: this.selectedConversationId,
            languageCode: this.selectedConversation.languageCode,
            redactionType,
            startTime,
            endTime,
            selectedText: selectedText ?? selection.selectedText,
            selectedChannel,
            phraseStartTime:
                selectedChannel === undefined
                    ? selection.blurb?.timeInterval?.[0]
                    : undefined,
            phraseEndTime:
                selectedChannel === undefined
                    ? selection.blurb?.timeInterval?.[1]
                    : undefined,
        });

        runInAction(() => {
            this.transcriptionStore?.clearLastSelection();
            this.transcriptionStore?.setSelectedStartTimestamp(null);
            this.transcriptionStore?.setSelectedEndTimestamp(null);
            this.transcriptionStore?.setSelectedEndTimestampTemp(null);
        });
        const didRefresh =
            await this.refreshSelectedConversationTranscriptAndAudio();
        if (!didRefresh) {
            this.messageStore.logError(
                "Redaction was saved, but the transcript and audio could not be refreshed. Refresh the page to see the latest redaction.",
            );
        }
    };

    private async refreshSelectedConversationTranscriptAndAudio() {
        if (!this.selectedConversationId || !this.selectedConversation) {
            return false;
        }

        const audioMetadataId = this.selectedConversationId;
        const refreshKey = Date.now().toString();
        const previousLanguageCode = this.selectedConversation.languageCode;

        try {
            const multiLangTranscriptions =
                await this.transcriptionService.getMulitLanguageTranscriptionByAmdId(
                    audioMetadataId,
                    { refreshKey },
                );

            runInAction(() => {
                if (
                    !this.selectedConversation ||
                    this.selectedConversationId !== audioMetadataId
                ) {
                    return;
                }

                this.selectedConversation.multiLangTranscriptions =
                    multiLangTranscriptions;
                this.selectedConversation.isRedacted = true;

                if (
                    multiLangTranscriptions.some(
                        (t) => t.languageCode === previousLanguageCode,
                    )
                ) {
                    this.selectedConversation.languageCode =
                        previousLanguageCode;
                } else if (
                    multiLangTranscriptions.some(
                        (t) =>
                            t.languageCode === UNREDACTED_ENGLISH_LANGUAGE_CODE,
                    )
                ) {
                    this.selectedConversation.languageCode =
                        UNREDACTED_ENGLISH_LANGUAGE_CODE;
                } else if (
                    multiLangTranscriptions.some(
                        (t) => t.languageCode === ENGLISH_LANGUAGE_CODE,
                    )
                ) {
                    this.selectedConversation.languageCode =
                        ENGLISH_LANGUAGE_CODE;
                } else {
                    this.selectedConversation.languageCode =
                        multiLangTranscriptions[0]?.languageCode ?? "";
                }

                if (
                    this.selectedConversation.audioTranscription &&
                    (this.selectedConversation.audioTranscription.phrases
                        ?.length ?? 0) > 0
                ) {
                    this.averageUtteranceDuration =
                        computeAverageUtteranceForTranscript(
                            this.selectedConversation.audioTranscription
                                .phrases,
                        );
                }

                this.transcriptionStore?.setAudioStartTimestamp(null);
                this.transcriptionStore?.setAudioEndTimestamp(null);
            });

            if (
                this.selectedConversationId === audioMetadataId &&
                !this.selectedConversation?.isChat
            ) {
                this.setSoundClipEditorStore(refreshKey);
            }

            return true;
        } catch {
            return false;
        }
    }

    @action
    loadMetadata(orgId?: string) {
        this.setupAsyncTask("Load Metadata Labels", async () => {
            if (orgId) {
                const metaLabels = await this.getMetaLabels();
                runInAction(() => {
                    if (metaLabels && Object.keys(metaLabels).length > 0) {
                        for (const [key, value] of Object.entries(metaLabels)) {
                            // keys are Meta1 to Meta5
                            this.allFilterCategories
                                .find(
                                    (cat) =>
                                        cat.title === addtlMetadataCatTitle,
                                )
                                ?.filters.forEach((filter) => {
                                    if (filter.filterTitle === key) {
                                        filter.filterTitle = value;
                                    }
                                });
                        }
                    }

                    this.setfilterLabels();
                });
            }
        });
    }

    @action
    loadExtendedMetadata(orgId?: string) {
        this.setupAsyncTask("Load Extended Metadata", async () => {
            if (!orgId) {
                return;
            }
            const extendedMetadataRes =
                await this.classifierService.getExtendedMetadataStringClassifiers();
            const existingMetaValues =
                this.applicationFiltersStore.extendedMetadata ?? {};

            // If call direction or client call id metadata is not present, remove the filter
            const callDirectionFieldName =
                OrganizationMetadataField[
                    OrganizationMetadataField.CallDirection
                ];
            const clientCallIdFieldName =
                OrganizationMetadataField[
                    OrganizationMetadataField.ClientCallId
                ];
            const hasCallDirectionField = coreFields.some(
                (field) => field.label === callDirectionFieldName,
            );
            const hasClientCallIdField = coreFields.some(
                (field) => field.label === clientCallIdFieldName,
            );
            this.setAllFilterCategories(
                this.allFilterCategories.map((cat) => {
                    let filters = cat.filters;
                    if (cat.title === basicFilterCatTitle) {
                        filters = filters.filter((filter) => {
                            switch (filter.storeObjectKey) {
                                case "callDirection":
                                    return hasCallDirectionField;
                                case "clientCallId":
                                    return hasClientCallIdField;
                                default:
                                    return true;
                            }
                        });
                    }
                    return {
                        ...cat,
                        filters,
                    };
                }),
            );

            runInAction(() => {
                const newExtendedMetadataFields = extendedMetadataRes.map(
                    (meta) => ({
                        id: meta.id,
                        name: meta.name,
                        isIndexed: meta.isIndexed,
                        classifierTypeName: ClassifierTypeName.ExtendedMetadata,
                        value: existingMetaValues[meta.name],
                    }),
                );
                const extendedMetadata = newExtendedMetadataFields.reduce(
                    (previous, current) => {
                        if (!current.value) return previous;
                        previous[current.name] = current.value;
                        return previous;
                    },
                    {},
                );
                this.applicationFiltersStore.setExtendedMetadata(
                    extendedMetadata,
                );

                this.setInitialExtendedMetadataFilterItems(
                    newExtendedMetadataFields,
                );

                this.updateAllTemporaryFilters();

                this.setfilterLabels();
            });
        });
    }

    @action
    setInitialExtendedMetadataFilterItems(
        extendedMetadataFields: IExtendedMetadataFilterItem[],
    ) {
        const extendedCategory = this.allFilterCategories.find(
            (category) => category.title === addtlMetadataCatTitle,
        );

        let extendedFields: ConversationFilter[] = extendedMetadataFields
            .filter(
                (item) =>
                    item.classifierTypeName ===
                    ClassifierTypeName.ExtendedMetadata,
            )
            .map((item) => ({
                filterTitle: item.name,
                isActive: false,
                id: uuidv4(),
                storeObjectKey: item.name,
                filterKey: `extendedMetadata.${item.name}`,
            }));

        if (extendedCategory) {
            const currentFilterTitles: string[] = extendedCategory.filters.map(
                (item) => item.filterTitle,
            );

            extendedFields.forEach((item) => {
                if (!currentFilterTitles.includes(item.filterTitle)) {
                    extendedCategory.filters.push(item);
                }
            });
        }
    }

    /**
     * The given categories with every filter the viewer sees as Deprecated collected into one
     * Legacy category at the end, the way the Signals filter picker groups its own.
     *
     * Membership is derived from the status each filter declares in `ApplicationFilterItemsStore`,
     * so a filter joins and leaves Legacy by changing that status and there is no membership list
     * here to keep in step. It is also viewer-specific: a tenant that cannot see a filter's
     * Deprecated tag gets no Legacy section and their categories look exactly as they did.
     *
     * Derived rather than stored, because `allFilterCategories` is what gets written to
     * localStorage and what the permission and metadata passes key off by title. A Legacy category
     * living in there would be a fourth category those passes have to know about, persisted per
     * user, and stale the moment a filter's status changed.
     *
     * Returns the input untouched when nothing qualifies, so the section simply does not appear.
     */
    withLegacyFilterCategory = (
        categories: ConversationFilterCategory[],
    ): ConversationFilterCategory[] => {
        const legacyFilters: ConversationFilter[] = [];
        const remainingCategories: ConversationFilterCategory[] = [];

        for (const category of categories) {
            const currentFilters = category.filters.filter((filter) => {
                const isLegacy =
                    this.applicationFilterItemsStore.isLegacyFilterKey(
                        filter.filterKey,
                    );
                if (!isLegacy) return true;
                legacyFilters.push(filter);
                return false;
            });

            if (currentFilters.length > 0)
                remainingCategories.push({
                    ...category,
                    filters: currentFilters,
                });
        }

        if (legacyFilters.length === 0) return categories;

        return [
            ...remainingCategories,
            {
                title: LEGACY_FILTER_GROUP_TITLE,
                subtitle: legacyFilterCatSubtitle,
                filters: legacyFilters,
            },
        ];
    };

    // Marks filters as temporarily shown if their current values are not the default
    @action
    updateTemporaryFilters = (filters: ConversationFilterCategory[]) => {
        return filters.map((category) => {
            return {
                ...category,
                filters: category.filters.map((filter) => {
                    const key = filter.storeObjectKey;
                    let shouldShow = false;
                    if (category.title === addtlMetadataCatTitle) {
                        if (key.startsWith("meta")) {
                            shouldShow = !!this.applicationFiltersStore[key];
                        } else {
                            const groupedItem =
                                this.applicationFiltersStore
                                    .extendedMetadataGrouped?.[key];
                            if (groupedItem) {
                                shouldShow = groupedItem.values.length > 0;
                            } else {
                                shouldShow =
                                    !!this.applicationFiltersStore
                                        .extendedMetadata?.[key];
                            }
                        }
                    } else {
                        shouldShow =
                            !this.applicationFiltersStore.isDefaultFilterValue(
                                key,
                            );
                    }

                    return {
                        ...filter,
                        isTemporarilyShown: !filter.isActive && shouldShow,
                    };
                }),
            };
        });
    };

    @action
    updateAllTemporaryFilters = () => {
        this.setAllFilterCategories(
            this.updateTemporaryFilters(this.allFilterCategories),
        );
    };

    @action
    setfilterLabels = () => {
        this.allFilterCategories.forEach(
            (category: ConversationFilterCategory) =>
                category.filters.forEach((filter: ConversationFilter) => {
                    filter.filterLabel = startCase(filter.filterTitle);
                }),
        );
    };

    @action
    resetConversations() {
        this.conversationsList = [];

        this.pageNumber = 1;
        this.selectedConversationId = undefined;
        this.estimatedConversationCount = undefined;
    }

    @action
    setSelectedSortOption = (sortOption?: SortOption) => {
        this.selectedSortOption = sortOption;
    };

    @action
    setSelectedConversationId = (id: string | undefined) => {
        this.selectedConversationId = id;
    };

    @action
    setSidePanel = (input: SidePanelType) => {
        this.sidePanel = input;
    };

    @action
    getSingleConversation = (id?: string) => {
        this.loadConversationFailed = false;
        // Abort any ongoing asynchronous task if it exists
        this.abortController.abort();
        if (id) {
            // Create a new AbortController for this task
            this.abortController = new AbortController();
            // get conversation data
            this.setupAsyncTask(loadConversation, async () => {
                try {
                    const conversationRes =
                        await this.conversationService.getConversation(
                            id,
                            this.abortController.signal,
                        );

                    const speakerIDResult =
                        conversationRes.conversationClassifiers.find(
                            (c) => c.name === ML_SpeakerIdentification,
                        )?.result;

                    if (!!speakerIDResult) {
                        const parsedResult = JSON.parse(speakerIDResult);
                        this.speakerStore.speakerIdentificationModelResults =
                            parsedResult;
                    } else {
                        this.speakerStore.speakerIdentificationModelResults =
                            undefined;
                    }
                    if (
                        conversationRes.workflowInstanceIds?.length &&
                        this.sidePanel === SidePanelType.Workflow
                    ) {
                        this.convoWorkflowStore.licensedModules = [];
                        this.convoWorkflowStore.uiModels = [];
                        this.convoWorkflowStore.getClassifierWorkflowInfo(
                            conversationRes.workflowInstanceIds,
                            this.selectedConversationId,
                        );
                    }

                    const hierLevels =
                        await this.organizationService.getInteractionHierarchyLevels(
                            conversationRes.hierarchyId,
                        );

                    this.getSoundClipsByAudioMetadataId(
                        conversationRes.conversationId,
                    );

                    this.getConversationSegments(
                        conversationRes.conversationId,
                    );

                    let multiLangTranscriptions: IMultiLanguageTranscriptionPayload[] =
                        [];
                    const isChatConversation =
                        conversationRes.mediaType === InteractionType.Chat ||
                        conversationRes.mediaType ===
                            InteractionType.ProcessedChat;

                    if (!isChatConversation) {
                        try {
                            multiLangTranscriptions =
                                await this.transcriptionService.getMulitLanguageTranscriptionByAmdId(
                                    id,
                                );
                        } catch (err: any) {
                            if (
                                err.message &&
                                err.message.includes(
                                    "Translation is not yet supported",
                                )
                            ) {
                                this.messageStore.logError(err.errorMessage);
                            } else if (
                                err.message &&
                                !err.message.includes(
                                    "Could not find Transcription for AudioMetadata Id",
                                )
                            ) {
                                this.messageStore.logError(
                                    "Failed to get Transcription: " +
                                        JSON.stringify(err),
                                );
                            }
                        }
                    }

                    runInAction(() => {
                        this.selectedConversation = new Conversation(
                            conversationRes,
                        );
                        this.hierarchyLevels = hierLevels;
                        this.selectedConversation.multiLangTranscriptions =
                            multiLangTranscriptions;

                        if (this.selectedConversation.isChat) {
                            const multiLangChatTranscriptions =
                                this.selectedConversation
                                    .multiLanguageChatTranscription ?? [];
                            if (
                                multiLangChatTranscriptions.some(
                                    (t) =>
                                        t.languageCode ===
                                        UNREDACTED_ENGLISH_LANGUAGE_CODE,
                                )
                            ) {
                                this.selectedConversation.languageCode =
                                    UNREDACTED_ENGLISH_LANGUAGE_CODE;
                            } else if (
                                multiLangChatTranscriptions.some(
                                    (t) =>
                                        t.languageCode ===
                                        ENGLISH_LANGUAGE_CODE,
                                )
                            ) {
                                this.selectedConversation.languageCode =
                                    ENGLISH_LANGUAGE_CODE;
                            } else {
                                // If English or UnRedacted English is not included, set the language code
                                // to the first transcription's language code
                                this.selectedConversation.languageCode =
                                    multiLangChatTranscriptions[0]
                                        ?.languageCode ?? "";
                            }
                        } else {
                            if (
                                multiLangTranscriptions.some(
                                    (t) =>
                                        t.languageCode ===
                                        UNREDACTED_ENGLISH_LANGUAGE_CODE,
                                )
                            ) {
                                this.selectedConversation.languageCode =
                                    UNREDACTED_ENGLISH_LANGUAGE_CODE;
                            } else if (
                                multiLangTranscriptions.some(
                                    (t) =>
                                        t.languageCode ===
                                        ENGLISH_LANGUAGE_CODE,
                                )
                            ) {
                                this.selectedConversation.languageCode =
                                    ENGLISH_LANGUAGE_CODE;
                            }
                            // If English or UnRedacted English is not included, set the language code
                            // to the first transcription's language code
                            else {
                                this.selectedConversation.languageCode =
                                    multiLangTranscriptions[0]?.languageCode ??
                                    "";
                            }
                        }
                        this.setCurrentEval();
                        this.setAgentCoaching();

                        if (
                            this.selectedConversation.audioTranscription &&
                            (this.selectedConversation.audioTranscription
                                .phrases?.length ?? 0) > 0
                        ) {
                            this.averageUtteranceDuration =
                                computeAverageUtteranceForTranscript(
                                    this.selectedConversation.audioTranscription
                                        .phrases,
                                );
                        }
                    });
                    return conversationRes;
                } catch (e: any) {
                    this.loadConversationFailed = true;
                    if (
                        e instanceof ServiceError &&
                        e.response.status === 403 &&
                        this.handleUnauthorizedError
                    ) {
                        this.handleUnauthorizedError(e);
                    } else {
                        if (e.name === "AbortError") {
                            // Handle the request cancellation
                            console.log("Request was canceled");
                        } else if (
                            e instanceof ServiceError &&
                            e.errorMessage
                        ) {
                            this.messageStore.logError(
                                "Failed to get conversation: " +
                                    (e as ServiceError).errorMessage,
                            );
                        } else {
                            // Handle other errors
                            this.messageStore.logError(
                                "Failed to get conversation: " + e.toString(),
                            );
                        }
                    }
                }
            });
        } else {
            // reset conversation data
            this.selectedConversation = null;
            this.setCurrentEval();
            this.setAgentCoaching();
        }
    };

    @action
    setCurrentEval = async () => {
        this.setupAsyncTask(loadCurrentEval, async () => {
            if (
                this.selectedConversationId &&
                this.selectedConversation?.evaluationId &&
                this.selectedConversation?.evaluationId !== emptyGUID &&
                this.authStore.orgStore.selectedOrganization?.id
            ) {
                try {
                    const { evaluation } =
                        await this.evaluationService.getEvalGraph(
                            this.authStore.orgStore.selectedOrganization.id,
                            this.selectedConversation?.evaluationId,
                        );
                    this.currentEval = Evaluation.fromJson(
                        evaluation as Evaluation,
                    );
                } catch (e) {
                    if (e instanceof Error) {
                        this.rootStore
                            .getStore(MessageStore)
                            .logError(e.message);
                    }
                }
            } else {
                this.currentEval = null;
            }
        });
    };

    @action
    setAgentCoaching = async () => {
        const conversationId = this.selectedConversationId;

        return this.setupAsyncTask(loadAgentCoaching, async () => {
            if (conversationId) {
                try {
                    const agentCoaching =
                        await this.agentCoachingService.getCoachingByConversation(
                            conversationId,
                        );
                    if (this.selectedConversationId !== conversationId) {
                        return false;
                    }
                    runInAction(() => {
                        this.agentCoaching = agentCoaching;
                    });
                    return true;
                } catch (e) {
                    if (e instanceof Error) {
                        this.rootStore
                            .getStore(MessageStore)
                            .logError(e.message);
                    }
                    return false;
                }
            } else {
                this.agentCoaching = [];
                return true;
            }
        });
    };

    @action
    setSoundClipEditorStore = (downloadMediaRefreshKey?: string) => {
        this.soundClipEditorStore?.dispose();
        this.soundClipEditorStore = new SoundClipEditorStore(
            "conversation-sound-clip-editor",
            this.currentClip as any,
            undefined,
            {
                onClipGenerated: this.onAudioClipCreated,
                onClipUpdated: this.onAudioClipUpdated,
                segmentList: this.clipsToSegmentList,
                downloadMediaFileId: this.selectedConversationId,
                downloadMediaRefreshKey,
                disableClip: false,
            },
        );
    };

    @action setConversationIds(ids?: string[]) {
        this.conversationIds = ids;
    }

    @action
    getConversations = async () => {
        this.resetAbortController();
        this.loadConversationsFailed = false;
        await this.setupAsyncTask(loadConversations, async () => {
            try {
                this.applicationFiltersStore.currentlyAppliedFilters =
                    _.cloneDeep(this.applicationFiltersStore.toFilterObject());

                const filters = this.applicationFiltersStore.toRequestObject();
                const sanitizedFilters =
                    this.applicationFilterItemsStore.stripInaccessibleFilters(
                        filters,
                        { requestObject: true },
                    ).filters;

                let conversationIds: string[] | undefined = undefined;
                if (this.conversationIds && this.conversationIds.length > 0) {
                    conversationIds = this.conversationIds;
                }

                this.isAppliedFilterCountForLastRequestGreaterThanZero =
                    this.applicationFiltersStore.appliedFilterCount > 0;

                const res = await this.conversationService.getConversations(
                    {
                        ...sanitizedFilters,
                        // Explicitly use the datePickerStore dates here to avoid getting out of sync e.g. auto-correct when start date after end date
                        startDate:
                            this.applicationFiltersStore.quickFiltersStore
                                .datePickerStore.beginDate,
                        endDate:
                            this.applicationFiltersStore.quickFiltersStore
                                .datePickerStore.endDate,
                        conversationIds,
                        pageNumber: this.pageNumber ?? 1,
                        sortField: this.selectedSortOption?.sortField,
                        sortDirection: this.selectedSortOption?.sortDirection,
                    },
                    this.abortController.signal,
                );
                runInAction(() => {
                    this.conversationsList = res.conversationResults;
                    this.estimatedConversationCount = res.estimatedCount;
                    this.sqlQueries = res.sqlQueries;

                    if (res.visualizationData) {
                        const tempGrain = temporalGrain(
                            this.applicationFiltersStore.quickFiltersStore
                                .startDate!,
                            this.applicationFiltersStore.quickFiltersStore
                                .endDate!,
                        );
                        const countData = signalDataToGoogleFormat({
                            data: res.visualizationData?.data,
                            tooltipBuilder: (
                                x: string | number | null,
                                y: string | number | null,
                                seriesName: string | null,
                            ) =>
                                dataPointTooltipBuilder({
                                    x,
                                    y,
                                    series: seriesName,
                                    isTemporal: true,
                                    tempGrain,
                                    startDate:
                                        this.applicationFiltersStore
                                            .quickFiltersStore.startDate!,
                                    endDate:
                                        this.applicationFiltersStore
                                            .quickFiltersStore.endDate!,
                                }),
                            isComparePrev: false,
                            isTemporal: true,
                            temporalGrain: tempGrain,
                        });
                        this.conversationsOverTimeChartData = countData.data;
                    } else {
                        this.conversationsOverTimeChartData = [];
                    }
                });
            } catch (err: any) {
                this.loadConversationsFailed = true;
                if (err instanceof DOMException && err.name === "AbortError") {
                    // Request was cancelled, we don't need to log any errors
                    return;
                }
                if (
                    err.message &&
                    err.message.includes("Timeout during reading attempt")
                ) {
                    this.messageStore.logError(
                        "Failed to load conversations: " +
                            "Search was too large and timed out. Try running the search again in a shorter date range, or with simpler criteria.",
                    );
                } else {
                    this.messageStore.logError(
                        "Failed to load conversations: " + JSON.stringify(err),
                    );
                }
            }
        });
        this.getHighlights();
        this.getFavorites();
    };

    @action
    getFavorites = async () => {
        if (!this.favoriteTrackedGroupId) return;

        await this.setupAsyncTask(
            "Load Favorite Status For Conversations",
            async () => {
                try {
                    const conversationIds =
                        this.conversationsList?.map(
                            (conversation) => conversation.conversationId,
                        ) ?? [];

                    if (conversationIds.length === 0) return {};

                    if (
                        !!this.selectedConversationId &&
                        !conversationIds.includes(this.selectedConversationId!)
                    ) {
                        conversationIds.push(this.selectedConversationId!);
                    }

                    const res =
                        await this.userObjectTrackingService.getObjectTrackedStatus(
                            {
                                objectIds: conversationIds,
                                userTrackedGroupId: this.favoriteTrackedGroupId,
                            },
                        );

                    this.favoritedConversations = res;
                } catch (err: any) {
                    if (err) {
                        this.messageStore.logError(
                            "Failed to load conversation favorites",
                        );
                    }
                }
            },
        );
    };

    @action
    addFavorite = async (conversationId: string) => {
        await this.setupAsyncTask("Add Favorite Conversation", async () => {
            try {
                this.userObjectTrackingService.trackObjectForUser({
                    objectId: conversationId,
                    objectType: "conversation",
                    userTrackedGroupId: this.favoriteTrackedGroupId,
                });

                this.favoritedConversations = {
                    ...this.favoritedConversations,
                    [conversationId]: true,
                };
            } catch (err: any) {
                if (err) {
                    this.messageStore.logError(
                        "Failed to favorite conversation",
                    );

                    this.favoritedConversations = {
                        ...this.favoritedConversations,
                        [conversationId]: false,
                    };
                }
            }
        });
    };

    @action
    removeFavorite = async (conversationId: string) => {
        await this.setupAsyncTask("Add Favorite Conversation", async () => {
            try {
                this.userObjectTrackingService.unTrackObjectForUser({
                    objectId: conversationId,
                    objectType: "conversation",
                    userTrackedGroupId: this.favoriteTrackedGroupId,
                });

                this.favoritedConversations = {
                    ...this.favoritedConversations,
                    [conversationId]: false,
                };
            } catch (err: any) {
                if (err) {
                    this.messageStore.logError(
                        "Failed to unfavorite conversation",
                    );

                    this.favoritedConversations = {
                        ...this.favoritedConversations,
                        [conversationId]: true,
                    };
                }
            }
        });
    };

    hasFavorite = (conversationId: string) => {
        if (!this.favoritedConversations) return false;
        return !!this.favoritedConversations[conversationId];
    };

    @action
    getHighlights = async () => {
        this.resetAbortController();
        const applicationsFilterObject =
            this.applicationFiltersStore.toRequestObject();
        try {
            if (
                (applicationsFilterObject.wordsAndPhrasesSearchString ||
                    !!applicationsFilterObject.rbcFilterItems?.length) &&
                !!this.conversationsList?.length
            ) {
                const highlightRes =
                    await this.conversationService.getHighlights(
                        {
                            searchPhrase:
                                applicationsFilterObject.wordsAndPhrasesSearchString ??
                                "",
                            rbcFilterItems:
                                applicationsFilterObject.rbcFilterItems!,
                            conversationIds: this.conversationsList?.map(
                                (i) => i.conversationId,
                            ),
                        },
                        this.abortController.signal,
                    );
                runInAction(() => {
                    this.conversationsList?.forEach((c) => {
                        const foundHighlight = highlightRes.find(
                            (r) => r?.audioMetadataId === c.conversationId,
                        );
                        if (foundHighlight) {
                            c.highlights = foundHighlight;
                        }
                    });
                });
            }
        } catch (e) {
            // Do not display highlight errors to user
        }
    };

    @action
    getSearchConversations = () => {
        this.pageNumber = 1;
        this.isInitialLoad = false;
        this.selectedContainsClassifierIds =
            this.applicationFiltersStore.rbcFilterItems
                .filter(
                    // filter on value because we only care about classifier ids in contains blocks
                    (item) => item.value,
                )
                .flatMap((item) => item.classifierIds);

        this.setConversationIds(undefined);
        this.getConversations();
    };

    @action
    setPageNumberAndGetConversations = (value: number) => {
        this.pageNumber = value;
        this.getConversations();
    };

    @action
    setIsInitialLoad = (input: boolean, fromAgent?: boolean) => {
        this.isInitialLoad = input;
        if (fromAgent !== undefined) this.fromAgent = fromAgent;
    };

    async downloadClipFromBlob(filePath?: string) {
        if (filePath) {
            return await this.soundClipService.downloadAudioClipAsBlob(
                filePath,
                StorageAccountUseOptions.Main,
            );
        }
        return new Blob([], { type: "audio/wav" });
    }

    @action
    onAudioClipCreated = async (startTime: number, endTime: number) => {
        if (
            this.currentClip &&
            !isNullableType(startTime) &&
            !isNullableType(endTime)
        ) {
            const uniqueClipName = `Clip ${soundClipDurationDisplay(
                startTime,
            )} - ${soundClipDurationDisplay(endTime)}`;

            this.audioClip = { status: "loading" };

            const soundClip = generateClipForConversation(
                this.selectedConversation?.conversationId!,
                this.selectedConversation?.evaluationId,
                uniqueClipName,
                startTime,
                endTime,
                this.authStore._user?.profile.email,
            );

            try {
                this.transcriptionForClip(soundClip);
            } catch (err: any) {
                this.messageStore.logError(err.message);
                return;
            }

            this.createClip(soundClip);
        }
    };

    @action
    onAudioClipUpdated = async (soundClip: PartialSegment) => {
        if (
            this.currentClip &&
            !isNullableType(soundClip.startTime) &&
            !isNullableType(soundClip.endTime)
        ) {
            const uniqueClipName = `Clip ${soundClipDurationDisplay(
                soundClip.startTime,
            )} - ${soundClipDurationDisplay(soundClip.endTime)}`;

            this.audioClip = { status: "loading" };

            const existingClip = this.conversationSoundClips.find(
                (clip) => clip.soundClip.id === soundClip.id,
            );

            if (!existingClip) {
                return;
            }

            existingClip.soundClip.startTime = soundClip.startTime;
            existingClip.soundClip.endTime = soundClip.endTime;
            existingClip.soundClip.segmentName = uniqueClipName;

            if (!existingClip) {
                return;
            }

            try {
                this.transcriptionForClip(existingClip.soundClip);
            } catch (err: any) {
                this.messageStore.logError(err.message);
                return;
            }

            this.updateClip(existingClip);
        }
    };

    @action
    async createTextClip(clip: SoundClip) {
        if (!this.authStore.orgStore?.selectedOrganization?.id) {
            console.warn("Cannot create clip with empty organization Id");
            return;
        }

        this.setupAsyncTask(loadCreateClip, async () => {
            try {
                await this.soundClipService.createTextClip(
                    this.authStore.orgStore?.selectedOrganization?.id!,
                    clip,
                );

                this.getSoundClipsByAudioMetadataId(
                    this.selectedConversation?.conversationId!,
                );
                this.messageStore.logMessage(
                    "Created clip successfully",
                    "success",
                );
                this.audioClip = { status: "success" };
                setTimeout(() => {
                    this.audioClip = { status: "default" };
                }, 2000);
            } catch (err: any) {
                this.audioClip = { status: "error" };
                this.messageStore.logError(
                    "Failed to create clip: " + JSON.stringify(err),
                );
                setTimeout(() => {
                    this.audioClip = { status: "default" };
                }, 2000);
            }
        });
    }

    @action
    async createClip(clip: SoundClip) {
        if (!this.authStore.orgStore?.selectedOrganization?.id) {
            console.warn("Cannot create clip with empty organization Id");
            return;
        }

        this.setupAsyncTask(loadCreateClip, async () => {
            try {
                await this.soundClipService.createSoundClip(
                    this.authStore.orgStore?.selectedOrganization?.id!,
                    clip,
                );

                this.getSoundClipsByAudioMetadataId(
                    this.selectedConversation?.conversationId!,
                );
                this.messageStore.logMessage(
                    "Created clip successfully",
                    "success",
                );
                this.audioClip = { status: "success" };
                setTimeout(() => {
                    this.audioClip = { status: "default" };
                }, 2000);
            } catch (err: any) {
                this.audioClip = { status: "error" };
                this.messageStore.logError(
                    "Failed to create clip: " + JSON.stringify(err),
                );
                setTimeout(() => {
                    this.audioClip = { status: "default" };
                }, 2000);
            }
        });
    }

    @action
    async updateClip(clip: ConversationSoundClip) {
        if (!this.authStore.orgStore?.selectedOrganization?.id) {
            console.warn("Cannot create clip with empty organization Id");
            return;
        }

        this.setupAsyncTask(loadCreateClip, async () => {
            try {
                await this.soundClipService.updateSoundClip(
                    this.authStore.orgStore?.selectedOrganization?.id!,
                    clip.soundClip,
                );

                this.getSoundClipsByAudioMetadataId(
                    this.selectedConversation?.conversationId!,
                );
                this.messageStore.logMessage(
                    "Updated clip successfully",
                    "success",
                );
                this.audioClip = { status: "success" };
                setTimeout(() => {
                    this.audioClip = { status: "default" };
                }, 2000);
            } catch (err: any) {
                this.audioClip = { status: "error" };
                this.messageStore.logError(
                    "Failed to create clip: " + JSON.stringify(err),
                );
                setTimeout(() => {
                    this.audioClip = { status: "default" };
                }, 2000);
            }
        });
    }

    @action
    async deleteClip(soundClip?: SoundClip) {
        if (soundClip) {
            this.setupAsyncTask(loadDeleteClip, async () => {
                try {
                    await this.soundClipService.deleteClipById(
                        soundClip.id,
                        !!this.selectedConversation?.callDurationMillis,
                    );
                    this.getSoundClipsByAudioMetadataId(
                        this.selectedConversation?.conversationId!,
                    );
                    this.messageStore.logMessage(
                        "Deleted clip successfully",
                        "success",
                    );
                    this.audioClip = { status: "success" };
                    setTimeout(() => {
                        this.audioClip = { status: "default" };
                    }, 2000);
                } catch (err: any) {
                    this.audioClip = { status: "error" };
                    this.messageStore.logError(
                        "Failed to delete clip: " + JSON.stringify(err),
                    );
                    setTimeout(() => {
                        this.audioClip = { status: "default" };
                    }, 2000);
                }
            });
        }
    }

    @action
    private transcriptionForClip(soundClip: SoundClip) {
        if (this.selectedConversation?.audioTranscription === undefined) {
            return;
        }

        let txSegmentModels: ITranscriptionSegment[];

        if (this.selectedConversation.languageCode !== "en") {
            // Use english transcript from multiLang instead of selectedAudioTranscript
            const engTranscript = getTranscriptByLangCode(
                "en",
                this.selectedConversation.multiLangTranscriptions,
            );

            if (!engTranscript) {
                console.warn(
                    "No english transcript found for multi-language transcription",
                );
                throw new Error(
                    "No english transcript found for multi-language transcription",
                );
            }

            //if we have a english translation
            if (engTranscript) {
                txSegmentModels = engTranscript?.words ?? [];
            }
            //use the original language
            else {
                const nonEngTranscript = getTranscriptByLangCode(
                    this.selectedConversation.languageCode,
                    this.selectedConversation.multiLangTranscriptions,
                );

                if (!nonEngTranscript) {
                    console.warn(
                        `No transcript found for language: ${this.selectedConversation.languageCode}`,
                    );
                    throw new Error(
                        `No transcript found for language: ${this.selectedConversation.languageCode}`,
                    );
                }

                txSegmentModels = nonEngTranscript?.words ?? [];
            }

            matchTranscriptionToSoundClip(soundClip, txSegmentModels);
        } else {
            const txSegmentModels: ITranscriptionSegment[] =
                this.selectedConversation?.audioTranscription.words ?? [];
            matchTranscriptionToSoundClip(soundClip, txSegmentModels);
        }
    }

    @action
    getSoundClipsByAudioMetadataId(audioMetadataId: string) {
        if (audioMetadataId !== emptyGUID) {
            this.setupAsyncTask(loadConversationSoundClips, async () => {
                try {
                    const res =
                        await this.conversationService.getConversationSoundClipByAudioMetadataId(
                            audioMetadataId,
                        );

                    runInAction(() => {
                        res.sort((a, b) =>
                            a.soundClip.startTime > b.soundClip.startTime
                                ? 1
                                : -1,
                        );
                        this.conversationSoundClips = res.map(
                            ({ soundClip, ...rest }) => ({
                                soundClip: SoundClip.fromJson(soundClip),
                                ...rest,
                            }),
                        );
                    });
                } catch (err: any) {
                    this.messageStore.logError(
                        "Failed to get sound clips: " + JSON.stringify(err),
                    );
                }
            });
        }
    }

    @action
    getConversationSegments = async (conversationId: string) => {
        this.setupAsyncTask(loadConversationSegments, async () => {
            try {
                const result =
                    await this.conversationService.getConversationSegments(
                        conversationId,
                    );
                const segments: ConversationSegmentItem[] = Object.entries(
                    result,
                )
                    .flatMap(([heading, ranges]) =>
                        ranges.map(([startWordIndex, endWordIndex]) => ({
                            id: uuidv4(),
                            heading: formatSegmentHeading(heading),
                            startWordIndex,
                            endWordIndex,
                        })),
                    )
                    .sort((a, b) => a.startWordIndex - b.startWordIndex);

                runInAction(() => {
                    this.conversationSegments = segments;
                });
            } catch (err) {
                console.error("Failed to get conversation segments", err);
            }
        });
    };

    @computed
    get clipsToSegmentList(): PartialSegment[] {
        const activeUniqueSoundClips = uniqBy(
            this.conversationSoundClips.map((clip) => clip.soundClip),
            (arg) => arg.segmentName,
        );
        return activeUniqueSoundClips?.map(
            ({ startTime, endTime, id, segmentName }) => ({
                startTime,
                endTime,
                id,
                labelText: segmentName,
            }),
        );
    }

    @action
    setUnsavedFilters = (input: SavedFilter[]) => {
        // Need to create new copy of input so that UI refreshes when canceling changes
        this.unsavedFilters = [...input];
    };

    @action
    setAllFilterCategories = (input: ConversationFilterCategory[]) => {
        this.allFilterCategories = input;
    };

    @action
    playBlurb = (playerOptions: PlayerOptions) => {
        this.playerOptions = playerOptions;
    };

    @action
    toggleManageFilterInput = () => {
        this.filterInputManagerIsOpen = !this.filterInputManagerIsOpen;
    };

    @action
    changeTranscriptionLanguage(languageCode: string) {
        if (this.selectedConversation) {
            this.selectedConversation.languageCode = languageCode;
        }
    }

    @action
    onTextMediaClipCreated = async (startIndex: number, endIndex: number) => {
        // UI-selected text can come from untranslated/unredacted views; rebuild from redacted english instead.
        const englishChatTranscript =
            this.selectedConversation?.multiLanguageChatTranscription?.find(
                (item) => item.languageCode === ENGLISH_LANGUAGE_CODE,
            )?.chatTranscription;

        if (!englishChatTranscript) {
            this.rootStore
                .getStore(MessageStore)
                .logError(
                    "No english redacted transcript found. Unable to create text clip.",
                );
            return;
        }

        const text = this.buildTextClipFromChatRange(
            englishChatTranscript,
            startIndex,
            endIndex,
        );

        if (text.length > 6000) {
            this.rootStore
                .getStore(MessageStore)
                .logError("Text clips may not exceed 6k characters");
            return;
        }

        const uniqueClipName = `Clip ${textClipTimestampDisplay(
            englishChatTranscript[startIndex]?.timestamp ?? "",
        )} - ${textClipTimestampDisplay(
            englishChatTranscript[endIndex]?.timestamp ?? "",
        )}`;
        const existingInactive = this.currentEval?.soundClips?.find(
            (s) => s.segmentName === uniqueClipName && !s.isActive,
        );
        if (existingInactive) {
            existingInactive.isActive = true;
            return;
        }
        const user = await this.authStore.getUserObject();

        const soundClip = generateClipForConversation(
            this.selectedConversationId!,
            this.currentEval?.id,
            uniqueClipName,
            startIndex,
            endIndex,
            user?.profile.email,
            true,
        );
        soundClip.transcriptionText = text;

        this.currentEval?.soundClips?.push(soundClip);

        this.setupAsyncTask(createAudioClipTask, () =>
            this.setupAsyncTask(
                `${createAudioClipTask}-${soundClip.segmentName}`,
                () => this.createTextClip(soundClip),
            ).catch((reason) => {}),
        );
    };

    private buildTextClipFromChatRange(
        chatTranscription: IChatRecord[],
        startIndex: number,
        endIndex: number,
    ) {
        const textForClip: string[] = [];
        for (let i = startIndex; i <= endIndex; i++) {
            const message = chatTranscription[i]?.text ?? "";
            textForClip.push(message.endsWith(".") ? message : `${message}.`);
        }

        return textForClip.join(" ");
    }

    @action
    setActiveClip = (id?: string, start?: number, end?: number) => {
        this.activeClip = this.currentEval?.soundClips?.find(
            (value) => value.id === id,
        );
    };

    @action
    toggleEvaluateConversationDialogOpen = () => {
        this.evaluateConversationDialogOpen =
            !this.evaluateConversationDialogOpen;
    };

    @action
    toggleEvaluationSuccessDialogOpen = () => {
        this.evaluateSuccessDialogOpen = !this.evaluateSuccessDialogOpen;
    };

    @action
    toggleConversationsOverTimeExpanded = () => {
        this.isConversationsOverTimeChartExpanded =
            !this.isConversationsOverTimeChartExpanded;
    };

    async saveUnsavedFilters() {
        const ownedUnsavedFilters = this.unsavedFilters;

        const toUpdate: SavedFilter[] = [];
        const toDelete: string[] = [];

        for (const savedFilter of this.applicationFiltersStore
            .ownedSavedFilters) {
            const associatedUnsavedFilter = ownedUnsavedFilters.find(
                (filter) => filter.id === savedFilter.id,
            );

            if (!associatedUnsavedFilter) toDelete.push(savedFilter.id);
            else if (associatedUnsavedFilter.name !== savedFilter.name)
                toUpdate.push(associatedUnsavedFilter);
        }

        try {
            if (toDelete.length > 0)
                await this.applicationFiltersStore.applicationFiltersService.deleteApplicationFilters(
                    toDelete,
                );

            if (toUpdate.length > 0)
                await this.applicationFiltersStore.applicationFiltersService.updateApplicationFilters(
                    toUpdate,
                );

            this.applicationFiltersStore.setSavedFilters([
                ...this.applicationFiltersStore.notOwnedSavedFilters,
                ...this.unsavedFilters,
            ]);
        } catch (e: unknown) {
            if (e instanceof ServiceError && e.errorMessage) {
                this.messageStore.logError(e.errorMessage);
            }
        }
    }

    @computed
    get isFocused(): boolean {
        return !!this.selectedConversationId;
    }

    @computed
    get currentClip() {
        return this.selectedConversation;
    }

    @computed
    get canSaveFilters() {
        const newFilterLabels = this.unsavedFilters.map((item) => item.name);
        const newFilterLabelSet = new Set(newFilterLabels);
        return newFilterLabels.length !== newFilterLabelSet.size;
    }

    @computed
    get shownFilterItems() {
        // Grouped after the shown/hidden filter, so Legacy collects the deprecated filters the
        // viewer actually has open rather than appearing for ones they turned off.
        return this.withLegacyFilterCategory(
            this.allFilterCategories.map((category) => ({
                ...category,
                filters: category.filters.filter(
                    (filter) => filter.isActive || filter.isTemporarilyShown,
                ),
            })),
        );
    }

    @computed
    get inactiveFilterItems() {
        const filters = this.allFilterCategories
            .flatMap((item) => item.filters)
            .filter((item) => !item.isActive);

        return filters;
    }

    @computed
    get unredactedTranscript(): ITranscriptionSegment[] | undefined {
        const transcriptByLangCode = getTranscriptByLangCode(
            "en-unredacted",
            this.selectedConversation?.multiLangTranscriptions,
        );
        if (!transcriptByLangCode) {
            return undefined;
        }
        return transcriptByLangCode?.phrases ?? transcriptByLangCode?.words;
    }

    @computed
    get unredactedChatTranscript() {
        return this.selectedConversation?.multiLanguageChatTranscription?.find(
            (item) => item.languageCode === "en-unredacted",
        )?.chatTranscription;
    }

    @computed
    get activeSoundClips(): SoundClip[] | undefined {
        return this.conversationSoundClips
            ?.map((clip) => clip.soundClip)
            .filter((s) => s.isActive)
            .sort((a, b) => a.startTime - b.startTime)
            .slice();
    }

    @computed
    get activeSoundClipsNotAttachedToAnswer(): SoundClip[] | undefined {
        return this.activeSoundClips?.map((s) => {
            const soundClipHasAnswer = !!s.soundClipAnswers?.filter(
                (sca) => sca.isActive,
            )?.length;

            const soundClipUsedInLocalState = !!this.usedSoundClipIds.includes(
                s.id,
            );

            let soundClipIsUsable = false;

            if (!soundClipHasAnswer && !soundClipUsedInLocalState) {
                soundClipIsUsable = true;
            }

            if (!soundClipUsedInLocalState && soundClipHasAnswer) {
                // here we need to check if the soundClipAnswer answerId is currently in local state, this would mean it was removed locally but hasnt been saved
                // there should be only 1 sca per sc in convo data labeling
                const scaAnswerIds = s.soundClipAnswers?.map(
                    (sca) => sca.answerId,
                );
                this.convoDataLabelStore.answers
                    .map((a) => a.id)
                    .forEach((answerId) => {
                        if (scaAnswerIds?.includes(answerId)) {
                            soundClipIsUsable = true;
                        }
                    });
            }
            // this is for disabling the dropdown selection in convoMultiClip and ConvoClip
            (s as any).isDisabled = !soundClipIsUsable;

            return s;
        });
    }
    @computed
    get usedSoundClipIds(): string[] {
        return this.convoDataLabelStore.answers
            .flatMap((a) => a.soundClipAnswers)
            .filter((sca) => sca.isActive)
            .map((sc) => sc.soundClipId);
    }

    @computed
    get focusedConversationInsights() {
        return getFocusedConversationInsights(
            this.selectedConversation?.focusedConversationInsightSource ??
                null,
            this.selectedConversation?.conversationClassifiers ?? [],
        );
    }

    @computed
    get legacyTopicInsights() {
        return getLegacyTopicInsights(
            this.selectedConversation?.conversationClassifiers ?? [],
        );
    }

    // labels for meta 1-5
    async getMetaLabels() {
        return await this.metaLabelService.getMetaLabels();
    }

    public async getFocusedConversationAdditionalMetadata() {
        return this.setupAsyncTask("SingleConvoMetadata", async () => {
            const amdId = this.selectedConversationId;
            if (!amdId) return undefined;

            let audioMetadata: AudioMetadataModel =
                (await this.metadataService.getMetadataById(amdId)) ?? {};

            const extendedMetadata: Record<string, unknown> =
                await this.metadataService.getExtendedMetadataValuesById(amdId);

            return { audioMetadata, extendedMetadata };
        });
    }

    @computed
    get currentAgentCoaching() {
        return (
            this.agentCoaching.find(
                (coaching) =>
                    coaching.coachingStatus === AgentCoachingStatus.InProgress,
            ) ?? this.agentCoaching[0]
        );
    }

    @computed
    get currentUserIsDraftOwner() {
        return (
            this.currentAgentCoaching?.coachId ===
            this.authStore._user.profile.sub
        );
    }

    @computed
    get coachingContextLoading() {
        return (
            this.getTaskLoading(loadConversation) ||
            this.getTaskLoading(loadCurrentEval) ||
            this.getTaskLoading(loadAgentCoaching)
        );
    }

    @computed
    get isOpeningCoachingPanel() {
        return this.coachingPanelOpenRequestCount > 0;
    }

    @action
    openCoachingPanelWithLock = async (
        options: OpenCoachingPanelOptions = {},
    ): Promise<boolean> => {
        this.coachingPanelOpenRequestCount++;
        try {
            return await this.openCoachingPanelWithLockInternal(options);
        } finally {
            runInAction(() => {
                this.coachingPanelOpenRequestCount = Math.max(
                    0,
                    this.coachingPanelOpenRequestCount - 1,
                );
            });
        }
    };

    private openCoachingPanelWithLockInternal = async (
        options: OpenCoachingPanelOptions,
    ): Promise<boolean> => {
        const { refreshCoaching = false, retryLock = true } = options;
        const conversationId = this.selectedConversationId;
        if (
            !conversationId ||
            !this.selectedConversation ||
            this.selectedConversation.conversationId !== conversationId ||
            this.coachingContextLoading
        ) {
            return false;
        }

        if (refreshCoaching) {
            const refreshed = await this.setAgentCoaching();
            if (
                refreshed !== true ||
                this.selectedConversationId !== conversationId
            ) {
                return false;
            }
        }

        const agentId =
            this.currentEval?.interaction?.agentId ??
            this.selectedConversation.agentId;
        if (!agentId) return false;

        const hasEvaluation =
            !!this.selectedConversation.evaluationId &&
            this.selectedConversation.evaluationId !== emptyGUID;
        if (hasEvaluation && !this.currentEval) return false;

        const activeDraft =
            this.currentAgentCoaching?.coachingStatus ===
            AgentCoachingStatus.InProgress
                ? this.currentAgentCoaching
                : undefined;
        if (
            activeDraft &&
            activeDraft.coachId !== this.authStore._user.profile.sub
        ) {
            if (this.evalReviewStore.hasCoachingLock) {
                await this.evalReviewStore.releaseCoachingLock();
            }
            this.messageStore.logError(
                "Another user has coaching notes in draft.",
            );
            return false;
        }

        const existingCoaching = this.currentAgentCoaching;
        const initializedCoaching = this.evalReviewStore.agentCoaching;
        const existingCoachingId =
            existingCoaching?.id ?? existingCoaching?.agentCoachingId;
        const initializedCoachingId =
            initializedCoaching?.id ?? initializedCoaching?.agentCoachingId;
        const isCurrentContext =
            initializedCoaching?.agentId === agentId &&
            this.coachingCoversConversation(
                initializedCoaching,
                conversationId,
            ) &&
            initializedCoachingId === existingCoachingId;

        if (!isCurrentContext) {
            if (
                this.evalReviewStore.coachingLockStatus !==
                CoachingLockStatus.Idle
            ) {
                await this.evalReviewStore.releaseCoachingLock();
            }
            this.evalReviewStore.reset();
            this.initEvalReviewStore();
        }

        const coaching = this.evalReviewStore.agentCoaching;
        if (!coaching) return false;

        // A conversation opened without an evaluation has no target yet.
        if (!coaching.targets?.length) {
            coaching.targets = [
                { audioMetadataId: conversationId } as AgentCoachingTarget,
            ];
            coaching.audioMetadataId = conversationId;
        }

        if (
            coaching.agentId !== agentId ||
            !this.coachingCoversConversation(coaching, conversationId)
        ) {
            return false;
        }

        // Completed coaching is read-only and does not need an editing lock.
        if (this.evalReviewStore.isCoached) {
            this.setSidePanel(SidePanelType.Coaching);
            return true;
        }

        const hasFinalLockFailure =
            this.evalReviewStore.coachingLockStatus ===
                CoachingLockStatus.Denied ||
            this.evalReviewStore.coachingLockStatus ===
                CoachingLockStatus.AlreadyCoached ||
            this.evalReviewStore.coachingLockStatus ===
                CoachingLockStatus.Error;
        const acquired =
            !retryLock && hasFinalLockFailure
                ? false
                : await this.evalReviewStore.acquireCoachingLock();
        if (this.selectedConversationId !== conversationId) {
            if (acquired) await this.evalReviewStore.releaseCoachingLock();
            return false;
        }

        if (!acquired) {
            if (
                this.evalReviewStore.agentCoaching !== coaching ||
                (this.evalReviewStore.coachingLockStatus !==
                    CoachingLockStatus.Denied &&
                    this.evalReviewStore.coachingLockStatus !==
                        CoachingLockStatus.AlreadyCoached &&
                    this.evalReviewStore.coachingLockStatus !==
                        CoachingLockStatus.Error)
            ) {
                return false;
            }
            if (this.evalReviewStore.claimCoachingLockFailureNotification()) {
                this.messageStore.logError(
                    this.evalReviewStore.coachingLockFailureMessage,
                );
            }
            return false;
        }

        this.setSidePanel(SidePanelType.Coaching);
        return true;
    };

    /**
     * A coaching belongs to the open conversation when it targets that
     * conversation directly, or when it targets the evaluation the conversation
     * produced. Evaluation-backed coaching has no conversation target of its own.
     */
    private coachingCoversConversation(
        coaching: AgentCoaching | undefined,
        conversationId: string,
    ): boolean {
        if (!coaching) return false;

        if (targetedConversationIds(coaching).includes(conversationId)) {
            return true;
        }

        const evaluationId =
            this.currentEval?.id ?? this.selectedConversation?.evaluationId;

        return (
            !!evaluationId &&
            evaluationId !== emptyGUID &&
            targetedEvaluationIds(coaching).includes(evaluationId)
        );
    }

    @action
    initEvalReviewStore = () => {
        const firstAgentCoaching = this.currentAgentCoaching;

        // draft owner editing a draft or viewing completed
        if (this.currentUserIsDraftOwner && !!firstAgentCoaching) {
            const agentCoaching = Object.assign(
                new AgentCoaching(),
                firstAgentCoaching,
            );

            this.evalReviewStore.initializeStoreFromConvos(
                agentCoaching.agentId,
                [agentCoaching],
                agentCoaching,
                agentCoaching.coachingStatus === AgentCoachingStatus.Completed,
            );
        }
        // non-draft owner, viewing completed
        if (!this.currentUserIsDraftOwner && !!firstAgentCoaching) {
            const agentCoaching = Object.assign(
                new AgentCoaching(),
                firstAgentCoaching,
            );
            this.evalReviewStore.initializeStoreFromConvos(
                agentCoaching.agentId,
                [agentCoaching],
                agentCoaching,
                true,
            );
        }

        // no coaching found, create new coaching
        if (!firstAgentCoaching) {
            const agentId =
                this.currentEval?.interaction?.agentId ??
                this.selectedConversation?.agentId;

            if (agentId) {
                const hasEval =
                    !!this.selectedConversation?.evaluationId &&
                    this.selectedConversation.evaluationId !== emptyGUID;

                if (hasEval && this.currentEval) {
                    // Evaluation-backed coaching
                    this.evalReviewStore.initializeStoreFromConvos(agentId, [
                        this.currentEval,
                    ]);
                } else {
                    // No-eval coaching: initialize from conversation
                    const coaching = new AgentCoaching();
                    coaching.agentId = agentId;
                    coaching.targets = [
                        {
                            audioMetadataId: this.selectedConversationId,
                        } as AgentCoachingTarget,
                    ];
                    coaching.audioMetadataId = this.selectedConversationId;
                    coaching.evaluationId = undefined;
                    // Default to InProgress so a conversation-only (no eval)
                    // coaching can be saved as a draft (AUT-10104). The final
                    // status is set on save (draft vs complete).
                    coaching.coachingStatus = AgentCoachingStatus.InProgress;

                    this.evalReviewStore.initializeStoreFromConvos(
                        agentId,
                        [],
                        coaching,
                        false,
                    );
                }
            }
        }
    };
}
